/*
 * KYEL — Code de bienvenue affiché après inscription (pop-up + encadré footer)
 * - Inscription sans rechargement (formulaire client natif Shopify envoyé en arrière-plan).
 *   Si Shopify force un envoi classique (protection anti-spam) : la page se recharge, puis on ramène
 *   la personne directement sur son code (pas de retour en haut de page).
 * - « Appliquer mon code » : même méthode que le tiroir panier (cart-discount.js) -> /cart/update.js { discount }.
 *   Aucun produit ajouté, aucune redirection. Panier non vide : le tiroir s'ouvre. Panier vide : message.
 * - « Copier le code » : presse-papier + « Copié ✓ ».
 * - Test : ?kyel_encart=succes affiche l'écran de succès sans soumettre le formulaire.
 * Le code et les textes se règlent dans l'éditeur de thème (section « KYEL encart -10 % » et bloc « Inscription e-mail »).
 */
import { DiscountUpdateEvent } from '@theme/events';

const ROOT = '[data-kyel-code]';
const TEST = /[?&]kyel_encart=succes(&|$)/.test(location.search);
const ROUTES_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

function msg(root, key, fallback) {
  return root.getAttribute('data-msg-' + key) || fallback;
}

function setStatus(root, text, isError) {
  const el = root.querySelector('[data-kyel-status]');
  if (!el) return;
  el.textContent = text || '';
  el.classList.toggle('is-error', !!isError);
}

function showSuccess(root, isTest) {
  root.querySelectorAll('[data-kyel-form-part]').forEach((el) => (el.hidden = true));
  root.querySelectorAll('[data-kyel-form-error]').forEach((el) => (el.hidden = true));
  const success = root.querySelector('[data-kyel-success]');
  if (success) success.hidden = false;
  root.setAttribute('data-kyel-state', 'ok');
  root.dispatchEvent(new CustomEvent('kyel:success', { bubbles: true, detail: { test: !!isTest } }));
}

function findRoot(form) {
  return form.closest(ROOT) || form.querySelector(ROOT);
}

function rememberSource(root) {
  try {
    sessionStorage.setItem('kyelSource', root.getAttribute('data-kyel-source') || '');
  } catch (_) {}
}

/* Mémorise quel encart est utilisé dès le clic sur « Je le veux » (avant tout autre script). */
document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const btn = target && target.closest('button[type="submit"], button:not([type])');
    const form = btn && btn.form;
    if (!form || !form.querySelector('input[name="contact[email]"]')) return;
    const root = findRoot(form);
    if (root) rememberSource(root);
  },
  true
);

/* ---------- Inscription sans rechargement ---------- */
document.addEventListener(
  'submit',
  async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.querySelector('input[name="contact[email]"]')) return;
    const root = findRoot(form);
    if (!root) return;
    rememberSource(root);

    event.preventDefault();
    const submitBtn = form.querySelector('button[type="submit"], button:not([type])');
    if (submitBtn) submitBtn.disabled = true;
    const nativeSubmit = () => HTMLFormElement.prototype.submit.call(form);

    try {
      const res = await fetch(form.getAttribute('action') || ROUTES_ROOT + 'contact', {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });
      const url = res.url || '';
      if (url.indexOf('/challenge') !== -1) return nativeSubmit();
      if (res.ok && url.indexOf('customer_posted=true') !== -1) {
        showSuccess(root, false);
        return;
      }
      root.querySelectorAll('[data-kyel-form-error]').forEach((el) => {
        el.hidden = false;
      });
    } catch (_) {
      return nativeSubmit();
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  },
  true
);

/* ---------- Appliquer mon code ---------- */
async function applyCode(root, button) {
  const code = (root.getAttribute('data-code') || '').trim();
  if (!code) return;
  button.disabled = true;
  setStatus(root, '');
  try {
    const cart = await fetch(ROUTES_ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) =>
      r.json()
    );
    const existing = (cart.discount_codes || [])
      .map((d) => d.code)
      .filter((c) => c && c.toUpperCase() !== code.toUpperCase());

    const res = await fetch(ROUTES_ROOT + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: existing.concat(code).join(',') }),
    });
    if (!res.ok) throw new Error('cart update ' + res.status);
    const data = await res.json();

    // Rafraîchit le tiroir panier (section « Réduction ») comme le fait le thème.
    document.dispatchEvent(new DiscountUpdateEvent(data, 'kyel-code'));

    const empty = !data.item_count;
    if (empty) {
      setStatus(root, msg(root, 'empty', 'Code appliqué ✓ – il sera déduit automatiquement de ta commande'));
    } else {
      setStatus(root, msg(root, 'applied', 'Code appliqué ✓'));
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') setTimeout(() => drawer.open(), 250);
    }
    button.textContent = msg(root, 'applied-button', 'Code appliqué ✓');
    root.dispatchEvent(new CustomEvent('kyel:applied', { bubbles: true, detail: { empty } }));
  } catch (_) {
    button.disabled = false;
    setStatus(root, msg(root, 'apply-error', 'Oups, ça n’a pas marché. Copie le code et colle-le au paiement.'), true);
  }
}

/* ---------- Copier le code ---------- */
async function copyCode(root, button) {
  const code = (root.getAttribute('data-code') || '').trim();
  if (!code) return;
  let ok = false;
  try {
    await navigator.clipboard.writeText(code);
    ok = true;
  } catch (_) {
    const ta = document.createElement('textarea');
    ta.value = code;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try {
      ok = document.execCommand('copy');
    } catch (_) {}
    ta.remove();
  }
  if (!ok) return;
  const label = button.getAttribute('data-label') || button.textContent;
  button.setAttribute('data-label', label);
  button.textContent = msg(root, 'copied', 'Copié ✓');
  setTimeout(() => (button.textContent = label), 2500);
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  const apply = target.closest('[data-kyel-apply]');
  const copy = target.closest('[data-kyel-copy]');
  if (!apply && !copy) return;
  const root = target.closest(ROOT);
  if (!root) return;
  event.preventDefault();
  if (apply) applyCode(root, apply);
  else copyCode(root, copy);
});

/* ---------- Retour après un envoi classique (rechargement) ---------- */
/* Shopify peut forcer un rechargement (protection anti-spam). On ramène alors la personne sur son code. */
function afterReload() {
  if (!/[?&]customer_posted=true/.test(location.search)) return;
  let src = '';
  try {
    src = sessionStorage.getItem('kyelSource') || '';
  } catch (_) {}
  const root = document.querySelector('[data-kyel-code][data-kyel-source="' + (src || 'footer') + '"]');
  const success = root && root.querySelector('[data-kyel-success]');
  if (root && success) {
    showSuccess(root, false);
    if (src !== 'popup') success.scrollIntoView({ block: 'center', behavior: 'instant' });
  }
  try {
    const url = new URL(location.href);
    url.searchParams.delete('customer_posted');
    url.hash = '';
    history.replaceState(history.state, '', url.toString());
  } catch (_) {}
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', afterReload);
else afterReload();

/* ---------- Mode test ---------- */
if (TEST) {
  const run = () => document.querySelectorAll(ROOT).forEach((root) => showSuccess(root, true));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
}
